import { InputField } from '@pickleballinc/react-ui';
import { forwardRef } from 'react';

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  disabled?: boolean;
  hasError?: boolean;
  hint?: string;
  label?: string;
  PrefixIcon?: () => React.ReactNode;
  SuffixIcon?: () => React.ReactNode;
  withDivider?: boolean;
  length?: number;
  decimalPlaces?: number;
  inputProps?: {
    className?: string;
    labelClassName?: string;
  };
}

export const Input = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    { inputProps = {}, length, maxLength, SuffixIcon, type, decimalPlaces = 2, onChange, ...props },
    ref
  ) => {
    // If length prop exists and maxLength exists but no SuffixIcon, create automatic counter
    const AutoSuffixIcon =
      length !== undefined && maxLength && !SuffixIcon
        ? () => (
            <div className='text-xs text-gray-500'>
              {length}/{maxLength}
            </div>
          )
        : SuffixIcon;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const originalEvent = { ...e };

      if (type === 'number') {
        // Remove non-numeric and non-decimal characters
        let value = e.target.value.replace(/[^0-9.]/g, '');

        // Ensure only one decimal point
        const parts = value.split('.');
        if (parts.length > 2) {
          value = parts[0] + '.' + parts.slice(1).join('');
        }

        // Limit to specified decimal places
        if (parts.length === 2 && parts[1] && parts[1].length > decimalPlaces) {
          value = parts[0] + '.' + parts[1].slice(0, decimalPlaces);
        }

        e.target.value = value;
      }

      onChange?.(originalEvent);
    };

    return (
      <InputField
        ref={ref}
        inputProps={{
          labelClassName: 'text-sm',
          ...inputProps,
        }}
        SuffixIcon={AutoSuffixIcon}
        maxLength={maxLength}
        {...props}
        onChange={handleChange}
      />
    );
  }
);

Input.displayName = 'Input';

export default Input;
